import { PERSON_FEATURE_COPY, PERSON_FEATURES } from '@app/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PAPER_UNIT_OPTIONS, usePaperUnit } from '../lib/paper-unit'
import { useTRPC } from '../lib/trpc'
import { Choice, FieldSection, Notice, SettingBlock, Toggle } from './index'

/**
 * The Studio features a person adds to their own Studio. Each takes effect as
 * it is flipped and reaches nobody else, so it is a switch rather than a field
 * with a Save.
 */
export function AccountFeatures() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const me = useQuery(trpc.auth.me.queryOptions())
  const set = useMutation(
    trpc.auth.setFeature.mutationOptions({
      onSuccess: () => qc.invalidateQueries(trpc.auth.me.queryFilter()),
    }),
  )
  const on = me.data?.features ?? []
  const [unit, setUnit] = usePaperUnit()

  return (
    <>
      <FieldSection
        title="Studio"
        description="Studio opens on your own documents. Add the areas you use; each is yours alone, and turning one off keeps everything in it."
      >
        {PERSON_FEATURES.map((feature) => {
          const copy = PERSON_FEATURE_COPY[feature]
          return (
            <SettingBlock
              key={feature}
              title={copy.label}
              description={copy.description}
            >
              <Toggle
                label={copy.label}
                hideLabel
                checked={on.includes(feature)}
                disabled={!me.data || set.isPending}
                onChange={(next) => set.mutate({ feature, on: next })}
              />
            </SettingBlock>
          )
        })}
        {set.error ? <Notice tone="error">{set.error.message}</Notice> : null}
      </FieldSection>
      <FieldSection title="Paper">
        <SettingBlock
          title="Size unit"
          description="A custom paper size is typed in this unit unless you pick another while setting it."
        >
          <Choice
            ariaLabel="Size unit"
            inline
            appearance="chip"
            options={PAPER_UNIT_OPTIONS}
            value={unit}
            onValueChange={setUnit}
          />
        </SettingBlock>
      </FieldSection>
    </>
  )
}
